import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PersonalPackGenerator from '../components/PersonalPackGenerator';
import PersonalPackHistory from '../components/PersonalPackHistory';
import {
    Calendar,
    ShoppingCart,
    ChefHat,
    Snowflake,
    AlertCircle,
    Heart,
    Clock,
    DollarSign,
    CheckCircle,
    ArrowRight,
    Loader2,
    Tag,
    Store,
    Percent,
    Sparkles,
    BookOpen,
    Lightbulb,
    Lock,
    Crown,
    Users,
    History,
    Download,
    FileText,
    HelpCircle,
    Plus,
    Trash2,
    Edit3,
    RotateCcw,
    Check,
    X,
    Wand2
} from 'lucide-react';

export const WeeklyPackPage = () => {
    const { user, isPremium } = useAuth();
    const navigate = useNavigate();
    const [pack, setPack] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('accueil');
    
    // Personal pack generator state
    const [showPackGenerator, setShowPackGenerator] = useState(false);
    const [showPackHistory, setShowPackHistory] = useState(false);
    const [personalPack, setPersonalPack] = useState(null);
    const [selectedHistoryPack, setSelectedHistoryPack] = useState(null);
    
    // User grocery list state
    const [userGroceryList, setUserGroceryList] = useState(null);
    const [editingGroceryList, setEditingGroceryList] = useState(false);
    const [newItemName, setNewItemName] = useState('');
    const [newItemQuantity, setNewItemQuantity] = useState('1');
    const [savingList, setSavingList] = useState(false);
    
    // Store filter state (Premium)
    const [selectedStores, setSelectedStores] = useState(new Set()); // Empty = all stores
    const [showStoreFilter, setShowStoreFilter] = useState(false);

    const isPreview = pack?.is_preview;

    useEffect(() => {
        loadPack();
    }, []);
    
    // Load user's personalized grocery list
    useEffect(() => {
        if (pack?.id && isPremium && !isPreview) {
            loadUserGroceryList();
        }
    }, [pack?.id, isPremium, isPreview]);
    
    const loadUserGroceryList = async () => {
        try {
            const response = await api.getUserGroceryList(pack.id);
            setUserGroceryList(response.data);
        } catch (error) {
            console.error('Error loading user grocery list:', error);
        }
    };
    
    const saveUserGroceryList = async () => {
        if (!userGroceryList) return;
        setSavingList(true);
        try {
            await api.updateUserGroceryList(pack.id, {
                items: userGroceryList.items,
                custom_items: userGroceryList.custom_items || []
            });
        } catch (error) {
            console.error('Error saving grocery list:', error);
        } finally {
            setSavingList(false);
        }
    };
    
    const toggleItemChecked = (index, isCustom = false) => {
        setUserGroceryList(prev => {
            if (isCustom) {
                const newCustomItems = [...(prev.custom_items || [])];
                newCustomItems[index] = { ...newCustomItems[index], checked: !newCustomItems[index].checked };
                return { ...prev, custom_items: newCustomItems };
            } else {
                const newItems = [...prev.items];
                newItems[index] = { ...newItems[index], checked: !newItems[index].checked };
                return { ...prev, items: newItems };
            }
        });
    };
    
    const addCustomItem = () => {
        if (!newItemName.trim()) return;
        setUserGroceryList(prev => ({
            ...prev,
            custom_items: [...(prev.custom_items || []), {
                item: newItemName.trim(),
                quantity: newItemQuantity.trim() || '1',
                checked: false,
                is_custom: true
            }]
        }));
        setNewItemName('');
        setNewItemQuantity('1');
    };
    
    const removeCustomItem = (index) => {
        setUserGroceryList(prev => ({
            ...prev,
            custom_items: (prev.custom_items || []).filter((_, i) => i !== index)
        }));
    };
    
    const removePackItem = (index) => {
        setUserGroceryList(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };
    
    const resetGroceryList = async () => {
        try {
            await api.resetUserGroceryList(pack.id);
            await loadUserGroceryList();
        } catch (error) {
            console.error('Error resetting list:', error);
        }
    };
    
    // Auto-save when list changes
    useEffect(() => {
        if (userGroceryList?.is_personalized || userGroceryList?.custom_items?.length > 0) {
            const timer = setTimeout(() => {
                saveUserGroceryList();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [userGroceryList]);

    const loadPack = async () => {
        try {
            const response = await api.getWeeklyPack();
            setPack(response.data);
        } catch (error) {
            console.error('Error loading weekly pack:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="font-medium text-muted-foreground">Chargement du pack...</p>
                </div>
            </div>
        );
    }

    if (!pack) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-foreground">
                        <Calendar className="w-10 h-10 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground text-lg">Pack non disponible</p>
                </div>
            </div>
        );
    }

    const allSections = [
        { id: 'accueil', label: 'Accueil', icon: Heart, color: 'text-pink-500' },
        { id: 'utilisation', label: 'Comment utiliser', icon: BookOpen, premium: true, color: 'text-blue-500' },
        { id: 'rabais', label: 'Rabais', icon: Tag, color: 'text-orange-500' },
        { id: 'epicerie', label: 'Liste', icon: ShoppingCart, premium: true, color: 'text-green-500' },
        { id: 'soupers', label: 'Soupers', icon: ChefHat, color: 'text-purple-500' },
        { id: 'congelation', label: 'Congélation', icon: Snowflake, premium: true, color: 'text-cyan-500' },
        { id: 'secours', label: 'Secours', icon: AlertCircle, premium: true, color: 'text-yellow-500' },
        { id: 'article', label: 'Article', icon: BookOpen, premium: true, color: 'text-rose-500', conditional: true },
        { id: 'recommandations', label: 'Nos Recommandations', icon: Sparkles, premium: true, color: 'text-indigo-500' },
    ];
    
    // Filter sections based on conditions (e.g., hide article if not present)
    const sections = allSections.filter(section => {
        if (section.conditional) {
            if (section.id === 'article') {
                return pack?.article_content && pack.article_content.trim() !== '';
            }
        }
        return true;
    });

    // Premium upgrade CTA component
    const PremiumCTA = () => (
        <div className="bg-gradient-cameleon rounded-3xl p-8 md:p-12 text-center my-8 border-4 border-foreground shadow-[8px_8px_0px_0px_#1e293b] relative overflow-hidden">
            {/* Decorative */}
            <div className="absolute top-4 left-4 w-16 h-16 border-4 border-white/20 rounded-full" />
            <div className="absolute bottom-4 right-4 w-12 h-12 border-4 border-white/20 rounded-2xl rotate-12" />
            
            <div className="relative z-10">
                <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-6">
                    <Lock className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-heading text-3xl md:text-4xl font-black text-white mb-4">
                    Contenu réservé aux Premium
                </h3>
                <p className="text-white/90 text-lg mb-8 max-w-lg mx-auto">
                    Débloquez le pack complet avec la liste d'épicerie, les 5 soupers planifiés, 
                    les astuces de congélation et bien plus!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                    <div className="bg-white rounded-2xl p-4 border-2 border-foreground shadow-pop">
                        <p className="text-sm text-muted-foreground font-medium">Hebdomadaire</p>
                        <p className="font-heading text-3xl font-black text-foreground">5,99$/sem</p>
                        <p className="text-sm text-green-600 font-bold">1ère semaine gratuite!</p>
                    </div>
                    <div className="bg-white rounded-2xl p-4 border-2 border-primary shadow-pop-green">
                        <p className="text-sm text-muted-foreground font-medium">Mensuel</p>
                        <p className="font-heading text-3xl font-black text-foreground">19,99$/mois</p>
                        <p className="text-sm text-green-600 font-bold">Économisez 4$/mois</p>
                    </div>
                </div>
                <Button 
                    onClick={() => navigate('/subscription')}
                    className="bg-white text-foreground hover:bg-white/90 rounded-full px-10 py-6 font-heading font-black text-lg border-2 border-foreground shadow-pop hover:shadow-pop-hover hover:-translate-y-1 transition-all"
                >
                    <Crown className="w-5 h-5 mr-2" />
                    Devenir Premium
                </Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-background" data-testid="weekly-pack-page">
            {/* Header Pack - Colorful gradient */}
            <header className="bg-gradient-cameleon text-white py-16 md:py-24 relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-10 left-10 w-24 h-24 border-4 border-white/20 rounded-full" />
                <div className="absolute bottom-10 right-10 w-20 h-20 border-4 border-white/20 rounded-3xl rotate-12" />
                <div className="absolute top-1/2 right-1/4 w-8 h-8 bg-white/10 rounded-full" />
                
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                    <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
                        <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur rounded-full font-heading font-bold text-sm">
                            <Calendar className="w-4 h-4" />
                            Pack #{pack.pack_number || pack.week_number}
                        </span>
                        {isPremium && (
                            <>
                                <Link 
                                    to="/packs/history"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur rounded-full font-heading font-bold text-sm transition-colors"
                                >
                                    <History className="w-4 h-4" />
                                    Historique
                                </Link>
                                <button 
                                    onClick={async () => {
                                        try {
                                            await api.downloadPackPdf('current');
                                        } catch (error) {
                                            console.error('Error downloading PDF:', error);
                                        }
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur rounded-full font-heading font-bold text-sm transition-colors"
                                    data-testid="download-pdf-btn"
                                >
                                    <Download className="w-4 h-4" />
                                    PDF
                                </button>
                            </>
                        )}
                        <Link 
                            to="/guide"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur rounded-full font-heading font-bold text-sm transition-colors"
                            data-testid="guide-link"
                        >
                            <HelpCircle className="w-4 h-4" />
                            Comment ça marche?
                        </Link>
                    </div>
                    <h1 className="font-heading text-5xl md:text-7xl font-black mb-4">
                        {pack.title}
                    </h1>
                    <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto">
                        {pack.description}
                    </p>
                </div>
            </header>

            {/* Navigation - Colorful pills */}
            <nav className="sticky top-16 z-40 bg-white/95 backdrop-blur border-b-2 border-foreground">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="flex overflow-x-auto gap-2 py-3 scrollbar-hide">
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => {
                                    if (section.premium && isPreview) {
                                        navigate('/subscription');
                                        return;
                                    }
                                    setActiveSection(section.id);
                                    document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap font-heading font-bold text-sm transition-all border-2 ${
                                    activeSection === section.id
                                        ? 'bg-foreground text-white border-foreground shadow-pop'
                                        : section.premium && isPreview
                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                            : 'bg-white text-foreground border-transparent hover:border-foreground'
                                }`}
                            >
                                <section.icon className={`w-4 h-4 ${activeSection === section.id ? 'text-white' : section.color}`} />
                                {section.label}
                                {section.premium && isPreview && <Lock className="w-3 h-3 ml-1" />}
                            </button>
                        ))}
                    </div>
                </div>
            </nav>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">
                {/* Accueil Pack */}
                <section id="accueil" className="scroll-mt-32">
                    <div className="card-pop p-8 md:p-12 bg-gradient-to-br from-pink-50 to-orange-50">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-16 h-16 bg-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0 border-2 border-foreground shadow-pop">
                                <Heart className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground mb-2">
                                    Bienvenue dans votre Pack!
                                </h2>
                                <p className="text-muted-foreground text-lg">Votre semaine de repas économiques commence ici</p>
                            </div>
                        </div>
                        <p className="text-lg text-foreground leading-relaxed">
                            {pack.intro_message}
                        </p>
                        
                        {/* Premium: Personal Pack Generator Button */}
                        {isPremium && !isPreview && (
                            <div className="mt-6 pt-6 border-t border-pink-200">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <Button
                                        onClick={() => setShowPackGenerator(true)}
                                        className="flex-1 gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-3 px-6 rounded-xl border-2 border-foreground shadow-[3px_3px_0px_0px_#1e293b]"
                                        data-testid="generate-personal-pack-btn"
                                    >
                                        <Wand2 className="w-5 h-5" />
                                        Créer mon pack IA
                                        <Crown className="w-4 h-4 text-yellow-300" />
                                    </Button>
                                    <Button
                                        onClick={() => setShowPackHistory(true)}
                                        variant="outline"
                                        className="gap-2 py-3 px-6 rounded-xl border-2 border-purple-300 hover:bg-purple-50"
                                        data-testid="pack-history-btn"
                                    >
                                        <History className="w-5 h-5 text-purple-500" />
                                        Mes packs précédents
                                    </Button>
                                </div>
                                <p className="text-sm text-muted-foreground mt-2">
                                    L'IA crée un pack sur mesure selon ton budget, ton foyer et tes goûts!
                                </p>
                            </div>
                        )}
                    </div>
                </section>
                
                {/* Personal Pack Generator Modal */}
                {showPackGenerator && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                        <div className="max-w-2xl w-full my-8">
                            <div className="flex justify-end mb-2">
                                <button 
                                    onClick={() => setShowPackGenerator(false)}
                                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center border-2 border-foreground shadow-pop hover:bg-gray-100"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <PersonalPackGenerator 
                                onPackGenerated={(pack) => setPersonalPack(pack)}
                                onClose={() => setShowPackGenerator(false)}
                            />
                        </div>
                    </div>
                )}

                {/* Personal Pack History Modal */}
                {showPackHistory && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                        <div className="max-w-2xl w-full my-8">
                            <PersonalPackHistory 
                                onClose={() => setShowPackHistory(false)}
                                onSelectPack={(pack) => {
                                    setSelectedHistoryPack(pack);
                                    setShowPackHistory(false);
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Selected History Pack Grocery List Modal */}
                {selectedHistoryPack && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                        <div className="max-w-2xl w-full my-8">
                            <Card className="border-2 border-green-200 shadow-xl">
                                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-4 rounded-t-lg flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <ShoppingCart className="w-5 h-5" />
                                        <span className="font-bold">Liste d'épicerie - {selectedHistoryPack.title}</span>
                                    </div>
                                    <button 
                                        onClick={() => setSelectedHistoryPack(null)}
                                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <CardContent className="p-4">
                                    <div className="max-h-96 overflow-y-auto space-y-2 mb-4">
                                        {selectedHistoryPack.grocery_list?.map((item, idx) => (
                                            <div 
                                                key={idx}
                                                className="flex items-center justify-between p-3 bg-white rounded-xl border hover:bg-gray-50"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-green-700 font-bold text-sm">
                                                        {item.count > 1 ? item.count : '✓'}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{item.item}</div>
                                                        <div className="text-sm text-muted-foreground">
                                                            {item.quantity} {item.unit}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="font-bold text-green-600">
                                                    ~{item.estimated_cost?.toFixed(2)}$
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="p-4 bg-green-100 rounded-xl border-2 border-green-300 flex justify-between items-center">
                                        <span className="font-bold">Total estimé</span>
                                        <span className="text-2xl font-black text-green-600">
                                            ~{selectedHistoryPack.estimated_total_cost?.toFixed(2)}$
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                )}

                {/* Utilisation Pack */}
                <section id="utilisation" className="scroll-mt-32">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                            <BookOpen className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground">
                            Comment utiliser ce pack
                        </h2>
                    </div>
                    {isPreview ? <PremiumCTA /> : (
                    <div className="space-y-6">
                        {/* Quick steps - Clickable */}
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <button 
                                onClick={() => document.getElementById('soupers')?.scrollIntoView({ behavior: 'smooth' })}
                                className="card-pop p-5 text-center hover:scale-105 transition-transform cursor-pointer"
                            >
                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-foreground">
                                    <span className="font-heading font-black text-white text-lg">1</span>
                                </div>
                                <h4 className="font-bold mb-1">Consultez les soupers</h4>
                                <p className="text-sm text-muted-foreground">5 recettes variées pour la semaine</p>
                            </button>
                            <button 
                                onClick={() => document.getElementById('rabais')?.scrollIntoView({ behavior: 'smooth' })}
                                className="card-pop p-5 text-center hover:scale-105 transition-transform cursor-pointer"
                            >
                                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-foreground">
                                    <span className="font-heading font-black text-white text-lg">2</span>
                                </div>
                                <h4 className="font-bold mb-1">Vérifiez les rabais</h4>
                                <p className="text-sm text-muted-foreground">Économisez sur vos ingrédients</p>
                            </button>
                            <button 
                                onClick={() => document.getElementById('epicerie')?.scrollIntoView({ behavior: 'smooth' })}
                                className="card-pop p-5 text-center hover:scale-105 transition-transform cursor-pointer"
                            >
                                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-foreground">
                                    <span className="font-heading font-black text-white text-lg">3</span>
                                </div>
                                <h4 className="font-bold mb-1">Imprimez la liste</h4>
                                <p className="text-sm text-muted-foreground">Liste d'épicerie complète</p>
                            </button>
                            <button 
                                onClick={() => document.getElementById('soupers')?.scrollIntoView({ behavior: 'smooth' })}
                                className="card-pop p-5 text-center hover:scale-105 transition-transform cursor-pointer"
                            >
                                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-foreground">
                                    <span className="font-heading font-black text-white text-lg">4</span>
                                </div>
                                <h4 className="font-bold mb-1">Cuisinez!</h4>
                                <p className="text-sm text-muted-foreground">Suivez les recettes étape par étape</p>
                            </button>
                        </div>
                        
                        {/* Tips box */}
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Lightbulb className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-blue-900 mb-2">Astuce pour maximiser vos économies</h4>
                                    <p className="text-blue-800 text-sm mb-3">
                                        Les articles en <strong className="text-orange-600">orange gras</strong> dans la liste d'épicerie correspondent à des rabais en cours. 
                                        Regroupez vos achats par magasin pour profiter de toutes les promotions!
                                    </p>
                                    <Link to="/guide" className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:underline">
                                        Voir le guide complet
                                        <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}
                </section>

                {/* Rabais Semaine */}
                <section id="rabais" className="scroll-mt-32">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-orange-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                            <Tag className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground">
                            Rabais de la semaine
                        </h2>
                    </div>
                    {pack.deals && pack.deals.length > 0 ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {pack.deals.map((deal, index) => (
                                <div key={deal.id} className="card-orange hover:-translate-y-2 hover:rotate-1 transition-all">
                                    <div className="relative h-36">
                                        <img
                                            src={deal.image_url || 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400'}
                                            alt={deal.product_name}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute top-3 right-3 bg-orange-500 text-white px-3 py-1.5 rounded-full text-sm font-bold border-2 border-foreground shadow-pop flex items-center gap-1">
                                            <Percent className="w-3 h-3" />
                                            -{deal.discount_percent}%
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                            <Store className="w-4 h-4" />
                                            {deal.store_name}
                                        </div>
                                        <p className="font-heading font-bold text-lg text-foreground mb-3">{deal.product_name}</p>
                                        <div className="flex items-center gap-3">
                                            <span className="text-3xl font-black text-orange-500">{deal.sale_price.toFixed(2)}$</span>
                                            <span className="text-muted-foreground line-through text-lg">{deal.original_price.toFixed(2)}$</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted-foreground text-lg">Aucun rabais cette semaine</p>
                    )}
                </section>

                {/* Liste Épicerie */}
                <section id="epicerie" className="scroll-mt-32">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                                <ShoppingCart className="w-7 h-7 text-white" />
                            </div>
                            <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground">
                                Ma liste d'épicerie
                            </h2>
                        </div>
                        {!isPreview && (
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline"
                                    className="btn-pop bg-white text-foreground" 
                                    onClick={() => setEditingGroceryList(!editingGroceryList)}
                                >
                                    {editingGroceryList ? <Check className="w-4 h-4 mr-2" /> : <Edit3 className="w-4 h-4 mr-2" />}
                                    {editingGroceryList ? 'Terminer' : 'Modifier'}
                                </Button>
                                <Button className="btn-pop bg-white text-foreground" onClick={() => window.print()}>
                                    <Download className="w-4 h-4 mr-2" />
                                    Imprimer
                                </Button>
                            </div>
                        )}
                    </div>
                    {isPreview ? <PremiumCTA /> : (
                    <div className="space-y-4">
                        {/* Savings banner if there are deals */}
                        {pack.total_savings > 0 && (
                            <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-4 border-2 border-foreground shadow-pop text-white flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                        <Sparkles className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-lg">Économies potentielles cette semaine!</p>
                                        <p className="text-white/80 text-sm">Grâce aux rabais en cours dans les épiceries</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-heading font-black text-3xl">{pack.total_savings?.toFixed(2) || '0.00'}$</p>
                                    <p className="text-white/80 text-sm">d'économies</p>
                                </div>
                            </div>
                        )}
                        
                        {/* Store filter - Premium only */}
                        {isPremium && (() => {
                            const groceryItems = userGroceryList?.items || pack.grocery_list || [];
                            const itemsWithDeals = groceryItems.filter(item => item.has_deal && item.deal_info);
                            const allStores = [...new Set(itemsWithDeals.map(item => item.deal_info?.store_name).filter(Boolean))].sort();
                            
                            if (allStores.length === 0) return null;
                            
                            return (
                                <div className="bg-purple-50 rounded-2xl p-4 border-2 border-purple-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <Store className="w-5 h-5 text-purple-600" />
                                            <span className="font-bold text-purple-700">Filtrer par épicerie</span>
                                            <Badge className="bg-purple-200 text-purple-700 border-0 text-xs">
                                                <Crown className="w-3 h-3 mr-1" />
                                                Premium
                                            </Badge>
                                        </div>
                                        <button
                                            onClick={() => setShowStoreFilter(!showStoreFilter)}
                                            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                                        >
                                            {showStoreFilter ? 'Masquer' : 'Afficher'}
                                        </button>
                                    </div>
                                    
                                    {showStoreFilter && (
                                        <>
                                            <p className="text-sm text-purple-600 mb-3">
                                                Sélectionnez les épiceries où vous souhaitez faire vos courses. Seuls les articles en rabais de ces magasins seront affichés.
                                            </p>
                                            <div className="flex flex-wrap gap-2 mb-3">
                                                {allStores.map(store => {
                                                    const isSelected = selectedStores.size === 0 || selectedStores.has(store);
                                                    const storeItemCount = itemsWithDeals.filter(item => item.deal_info?.store_name === store).length;
                                                    return (
                                                        <button
                                                            key={store}
                                                            onClick={() => {
                                                                const newSelected = new Set(selectedStores);
                                                                if (selectedStores.size === 0) {
                                                                    // First click: select only this store
                                                                    allStores.forEach(s => {
                                                                        if (s !== store) newSelected.add(s);
                                                                    });
                                                                    newSelected.delete(store);
                                                                    // Actually we want to keep only this store, so invert logic
                                                                    newSelected.clear();
                                                                    newSelected.add(store);
                                                                } else if (newSelected.has(store)) {
                                                                    newSelected.delete(store);
                                                                    if (newSelected.size === 0) {
                                                                        // If all deselected, show all
                                                                    }
                                                                } else {
                                                                    newSelected.add(store);
                                                                }
                                                                setSelectedStores(newSelected);
                                                            }}
                                                            className={`px-4 py-2 rounded-xl border-2 transition-all font-medium text-sm flex items-center gap-2 ${
                                                                isSelected
                                                                    ? 'border-purple-500 bg-purple-500 text-white shadow-lg'
                                                                    : 'border-purple-200 bg-white text-purple-400 hover:border-purple-300'
                                                            }`}
                                                            data-testid={`store-filter-${store}`}
                                                        >
                                                            <Store className="w-4 h-4" />
                                                            {store}
                                                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-white/20' : 'bg-purple-100'}`}>
                                                                {storeItemCount}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setSelectedStores(new Set())}
                                                    className="text-xs text-purple-600 hover:text-purple-800 underline"
                                                >
                                                    Tout afficher
                                                </button>
                                                <span className="text-purple-300">|</span>
                                                <button
                                                    onClick={() => setSelectedStores(new Set(allStores))}
                                                    className="text-xs text-purple-600 hover:text-purple-800 underline"
                                                >
                                                    Tout sélectionner
                                                </button>
                                            </div>
                                        </>
                                    )}
                                    
                                    {!showStoreFilter && selectedStores.size > 0 && (
                                        <p className="text-sm text-purple-600">
                                            {selectedStores.size} épicerie{selectedStores.size > 1 ? 's' : ''} sélectionnée{selectedStores.size > 1 ? 's' : ''}: {[...selectedStores].join(', ')}
                                        </p>
                                    )}
                                </div>
                            );
                        })()}
                        
                        {/* Add custom item form */}
                        {editingGroceryList && (
                            <div className="bg-blue-50 rounded-2xl p-4 border-2 border-blue-200">
                                <p className="text-sm text-blue-700 mb-3 font-medium">Ajoutez vos propres articles à la liste</p>
                                <div className="flex flex-wrap gap-2">
                                    <input
                                        type="text"
                                        className="flex-1 min-w-[150px] border-2 border-blue-300 rounded-xl px-4 py-2 focus:outline-none focus:border-blue-500"
                                        placeholder="Ex: Pain, Lait, Chips..."
                                        value={newItemName}
                                        onChange={(e) => setNewItemName(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && addCustomItem()}
                                    />
                                    <input
                                        type="text"
                                        className="w-24 border-2 border-blue-300 rounded-xl px-3 py-2 focus:outline-none focus:border-blue-500"
                                        placeholder="Qté"
                                        value={newItemQuantity}
                                        onChange={(e) => setNewItemQuantity(e.target.value)}
                                    />
                                    <Button 
                                        className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl"
                                        onClick={addCustomItem}
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Ajouter
                                    </Button>
                                </div>
                            </div>
                        )}
                        
                        <div className="card-green overflow-hidden">
                            {/* Mobile-friendly list view */}
                            <div className="divide-y-2 divide-green-200">
                                {/* Header */}
                                <div className="bg-green-100 p-3 flex items-center justify-between">
                                    <span className="font-heading font-bold text-sm">Articles</span>
                                    <span className="font-heading font-bold text-sm">Prix</span>
                                </div>
                                
                                {/* Custom items added by user */}
                                {(userGroceryList?.custom_items || []).map((item, index) => (
                                    <div key={`custom-${index}`} className="p-3 bg-blue-50 flex items-center gap-3">
                                        <button
                                            onClick={() => toggleItemChecked(index, true)}
                                            className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                                                item.checked 
                                                    ? 'bg-blue-500 border-blue-500' 
                                                    : 'border-blue-400 hover:border-blue-500'
                                            }`}
                                        >
                                            {item.checked && <Check className="w-4 h-4 text-white" />}
                                        </button>
                                        <div className="flex-1">
                                            <span className={`font-medium ${item.checked ? 'line-through text-gray-400' : ''}`}>
                                                {item.name}
                                            </span>
                                            {item.quantity && (
                                                <span className="text-gray-500 text-sm ml-2">({item.quantity})</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-blue-600 font-medium">Ajouté</span>
                                            {editingGroceryList && (
                                                <button
                                                    onClick={() => removeCustomItem(index)}
                                                    className="text-red-500 hover:text-red-700 p-1"
                                                    title="Supprimer"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Pack grocery items - Grouped by store for items with deals */}
                                {(() => {
                                    const groceryItems = userGroceryList?.items || pack.grocery_list || [];
                                    const itemsWithDeals = groceryItems.filter(item => item.has_deal && item.deal_info);
                                    const itemsWithoutDeals = groceryItems.filter(item => !item.has_deal || !item.deal_info);
                                    
                                    // Group items with deals by store
                                    const itemsByStore = {};
                                    itemsWithDeals.forEach(item => {
                                        const store = item.deal_info?.store_name || 'Autre';
                                        if (!itemsByStore[store]) {
                                            itemsByStore[store] = [];
                                        }
                                        itemsByStore[store].push(item);
                                    });
                                    
                                    // Filter stores based on selection (Premium feature)
                                    const allStoreNames = Object.keys(itemsByStore).sort();
                                    const storeNames = selectedStores.size === 0 
                                        ? allStoreNames 
                                        : allStoreNames.filter(store => selectedStores.has(store));
                                    
                                    return (
                                        <>
                                            {/* Items with deals, grouped by store */}
                                            {storeNames.map(storeName => (
                                                <div key={storeName}>
                                                    {/* Store header */}
                                                    <div className="bg-orange-100 p-2 px-3 flex items-center gap-2 border-t-2 border-orange-300">
                                                        <Tag className="w-4 h-4 text-orange-600" />
                                                        <span className="font-bold text-orange-700 text-sm">{storeName}</span>
                                                        <span className="text-xs text-orange-600">
                                                            ({itemsByStore[storeName].length} article{itemsByStore[storeName].length > 1 ? 's' : ''} en rabais)
                                                        </span>
                                                    </div>
                                                    {/* Items from this store */}
                                                    {itemsByStore[storeName].map((item, idx) => {
                                                        const originalIndex = groceryItems.findIndex(g => g.item === item.item);
                                                        return (
                                                            <div 
                                                                key={`${storeName}-${idx}`} 
                                                                className={`p-3 flex items-center gap-3 bg-orange-50 ${item.checked ? 'opacity-50' : ''}`}
                                                            >
                                                                <button
                                                                    onClick={() => userGroceryList && toggleItemChecked(originalIndex, false)}
                                                                    className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                                                                        item.checked 
                                                                            ? 'bg-green-500 border-green-500' 
                                                                            : 'border-orange-400 hover:border-orange-500'
                                                                    }`}
                                                                >
                                                                    {item.checked && <Check className="w-4 h-4 text-white" />}
                                                                </button>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className={`text-sm font-bold text-orange-600 ${item.checked ? 'line-through' : ''}`}>
                                                                        {item.item}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">{item.quantity}</div>
                                                                    <div className="flex flex-wrap items-center gap-1 mt-1">
                                                                        <span className="inline-flex items-center gap-1 text-[10px] bg-orange-500 text-white px-1.5 py-0.5 rounded-full">
                                                                            <Tag className="w-2.5 h-2.5" />
                                                                            -{item.deal_info.discount_percent}%
                                                                        </span>
                                                                        <span className="text-[10px] text-green-600 font-semibold">
                                                                            Économie: {item.deal_info.savings?.toFixed(2)}$
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                                    <span className="font-bold text-green-600 text-sm">{parseFloat(item.estimated_cost || 0).toFixed(2)}$</span>
                                                                    {editingGroceryList && (
                                                                        <button
                                                                            onClick={() => removePackItem(originalIndex)}
                                                                            className="text-red-500 hover:text-red-700 p-1"
                                                                            title="Retirer de ma liste"
                                                                        >
                                                                            <X className="w-4 h-4" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                            
                                            {/* Items without deals */}
                                            {itemsWithoutDeals.length > 0 && (
                                                <>
                                                    {storeNames.length > 0 && (
                                                        <div className="bg-gray-100 p-2 px-3 flex items-center gap-2 border-t-2 border-gray-300">
                                                            <ShoppingCart className="w-4 h-4 text-gray-600" />
                                                            <span className="font-bold text-gray-700 text-sm">Autres articles</span>
                                                            <span className="text-xs text-gray-500">
                                                                ({itemsWithoutDeals.length} article{itemsWithoutDeals.length > 1 ? 's' : ''})
                                                            </span>
                                                        </div>
                                                    )}
                                                    {itemsWithoutDeals.map((item, idx) => {
                                                        const originalIndex = groceryItems.findIndex(g => g.item === item.item);
                                                        return (
                                                            <div 
                                                                key={`other-${idx}`} 
                                                                className={`p-3 flex items-center gap-3 ${item.checked ? 'opacity-50' : ''}`}
                                                            >
                                                                <button
                                                                    onClick={() => userGroceryList && toggleItemChecked(originalIndex, false)}
                                                                    className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                                                                        item.checked 
                                                                            ? 'bg-green-500 border-green-500' 
                                                                            : 'border-green-400 hover:border-green-500'
                                                                    }`}
                                                                >
                                                                    {item.checked && <Check className="w-4 h-4 text-white" />}
                                                                </button>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className={`text-sm font-medium ${item.checked ? 'line-through' : ''}`}>
                                                                        {item.item}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">{item.quantity}</div>
                                                                </div>
                                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                                    <span className="font-bold text-green-600 text-sm">{parseFloat(item.estimated_cost || 0).toFixed(2)}$</span>
                                                                    {editingGroceryList && (
                                                                        <button
                                                                            onClick={() => removePackItem(originalIndex)}
                                                                            className="text-red-500 hover:text-red-700 p-1"
                                                                            title="Retirer de ma liste"
                                                                        >
                                                                            <X className="w-4 h-4" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                                
                                {/* Total */}
                                <div className="bg-green-500 text-white p-4 flex items-center justify-between">
                                    <span className="font-heading font-bold text-lg">Total</span>
                                    <span className="font-heading font-black text-2xl">
                                        {(pack.total_estimated_cost || pack.grocery_list?.reduce((sum, item) => sum + parseFloat(item.estimated_cost || 0), 0) || 0).toFixed(2)}$
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Reset button */}
                        {editingGroceryList && userGroceryList?.is_personalized && (
                            <div className="flex justify-center">
                                <Button 
                                    variant="outline" 
                                    className="text-gray-500"
                                    onClick={resetGroceryList}
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Réinitialiser la liste originale
                                </Button>
                            </div>
                        )}
                        
                        {/* Save indicator */}
                        {savingList && (
                            <div className="text-center text-sm text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                                Sauvegarde en cours...
                            </div>
                        )}
                    </div>
                    )}
                </section>

                {/* Soupers x5 */}
                <section id="soupers" className="scroll-mt-32">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-purple-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                            <ChefHat className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground">
                            {isPreview ? 'Aperçu des soupers' : 'Vos 5 soupers de la semaine'}
                        </h2>
                    </div>
                    <div className="space-y-6">
                        {(isPreview ? pack.soupers_preview || [] : pack.soupers || []).map((souper, index) => {
                            const recipe = souper.recipe;
                            const imageUrl = recipe?.image_url || souper.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400';
                            const title = recipe?.title || souper.title;
                            const prepTime = recipe?.prep_time || souper.prep_time || 0;
                            const cookTime = recipe?.cook_time || souper.cook_time || 0;
                            const totalTime = prepTime + cookTime;
                            const costPerServing = recipe?.cost_per_serving || souper.cost_per_serving || 0;
                            const difficulty = recipe?.difficulty;
                            const recipeId = souper.recipe_id || recipe?.id;
                            
                            // Different colors for each day
                            const dayColors = ['border-green-500 shadow-pop-green', 'border-orange-500 shadow-pop-orange', 'border-purple-500 shadow-pop-purple', 'border-cyan-500 shadow-[6px_6px_0px_0px_#06b6d4]', 'border-pink-500 shadow-[6px_6px_0px_0px_#ec4899]'];
                            const dayBgColors = ['bg-green-500', 'bg-orange-500', 'bg-purple-500', 'bg-cyan-500', 'bg-pink-500'];
                            
                            return (
                                <div 
                                    key={index} 
                                    className={`bg-white rounded-3xl border-2 ${dayColors[index % 5]} overflow-hidden hover:-translate-y-1 transition-all`}
                                >
                                    <div className="flex flex-col sm:flex-row">
                                        <div className="sm:w-64 h-52 sm:h-auto relative">
                                            <img
                                                src={imageUrl}
                                                alt={title}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className={`absolute top-4 left-4 ${dayBgColors[index % 5]} text-white px-4 py-2 rounded-full font-heading font-bold text-sm border-2 border-foreground shadow-pop`}>
                                                {souper.day}
                                            </div>
                                            {difficulty && (
                                                <div className="absolute bottom-4 left-4">
                                                    <Badge className={`${difficulty === 'facile' ? 'bg-green-100 text-green-700 border-green-400' : difficulty === 'moyen' ? 'bg-orange-100 text-orange-700 border-orange-400' : 'bg-purple-100 text-purple-700 border-purple-400'} border-2 font-bold`}>
                                                        {difficulty}
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 p-6">
                                            <h3 className="font-heading font-black text-2xl text-foreground mb-3">
                                                {title}
                                            </h3>
                                            {recipe?.description && (
                                                <p className="text-muted-foreground mb-4 line-clamp-2">
                                                    {recipe.description}
                                                </p>
                                            )}
                                            <div className="flex flex-wrap gap-4 mb-4">
                                                <span className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full text-sm font-medium">
                                                    <Clock className="w-4 h-4 text-blue-500" />
                                                    {totalTime} min
                                                </span>
                                                <span className="flex items-center gap-2 bg-green-100 px-3 py-1.5 rounded-full text-sm font-bold text-green-700">
                                                    <DollarSign className="w-4 h-4" />
                                                    {costPerServing.toFixed(2)}$/portion
                                                </span>
                                                {recipe?.servings && (
                                                    <span className="flex items-center gap-2 bg-purple-100 px-3 py-1.5 rounded-full text-sm font-medium text-purple-700">
                                                        <Users className="w-4 h-4" />
                                                        {recipe.servings} portions
                                                    </span>
                                                )}
                                            </div>
                                            {souper.tip && (
                                                <div className="flex items-start gap-2 bg-yellow-50 rounded-xl p-4 mb-4 border-2 border-yellow-200">
                                                    <Lightbulb className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                    <p className="text-sm text-yellow-800 font-medium">{souper.tip}</p>
                                                </div>
                                            )}
                                            {recipeId && !isPreview && (
                                                <Link to={`/recipes/${recipeId}`}>
                                                    <Button className="btn-pop bg-white text-foreground">
                                                        Voir la recette complète
                                                        <ArrowRight className="w-4 h-4 ml-2" />
                                                    </Button>
                                                </Link>
                                            )}
                                            {isPreview && recipeId && (
                                                <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                                                    <Lock className="w-4 h-4" />
                                                    Recette complète disponible avec Premium
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {isPreview && <PremiumCTA />}
                </section>

                {/* Congélation */}
                {!isPreview && (
                <section id="congelation" className="scroll-mt-32">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-cyan-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                            <Snowflake className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground">
                            Astuces congélation
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {(pack.freezing_tips || []).map((tip, index) => (
                            <div 
                                key={index}
                                className="flex items-start gap-4 bg-cyan-50 rounded-2xl p-5 border-2 border-cyan-200"
                            >
                                <div className="w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Snowflake className="w-5 h-5 text-white" />
                                </div>
                                <p className="text-foreground font-medium">{tip}</p>
                            </div>
                        ))}
                    </div>
                </section>
                )}

                {/* Recette Secours */}
                {!isPreview && pack.emergency_recipe && (
                <section id="secours" className="scroll-mt-32">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-yellow-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                            <AlertCircle className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground">
                            Recette secours
                        </h2>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-3xl border-2 border-yellow-400 shadow-[6px_6px_0px_0px_#eab308] p-6 md:p-10">
                        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                            <div>
                                <h3 className="font-heading font-black text-3xl text-foreground mb-2">
                                    {pack.emergency_recipe.title}
                                </h3>
                                <p className="text-muted-foreground text-lg">{pack.emergency_recipe.description}</p>
                            </div>
                            <div className="flex gap-3 mt-4 md:mt-0">
                                <span className="flex items-center gap-2 bg-yellow-200 px-4 py-2 rounded-full font-bold text-yellow-800 border-2 border-yellow-400">
                                    <Clock className="w-4 h-4" />
                                    {pack.emergency_recipe.time}
                                </span>
                                <span className="flex items-center gap-2 bg-green-200 px-4 py-2 rounded-full font-bold text-green-800 border-2 border-green-400">
                                    <DollarSign className="w-4 h-4" />
                                    {pack.emergency_recipe.cost}
                                </span>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div>
                                <h4 className="font-heading font-bold text-xl text-foreground mb-4">Ingrédients</h4>
                                <ul className="space-y-3">
                                    {(pack.emergency_recipe.ingredients || '').split(', ').filter(Boolean).map((ing, index) => (
                                        <li key={index} className="flex items-center gap-3">
                                            <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                                                <CheckCircle className="w-4 h-4 text-white" />
                                            </div>
                                            <span className="font-medium">{ing}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-heading font-bold text-xl text-foreground mb-4">Étapes</h4>
                                <ol className="space-y-3">
                                    {(pack.emergency_recipe.steps || '').split('. ').filter(Boolean).map((step, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <span className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white border-2 border-foreground">
                                                {index + 1}
                                            </span>
                                            <span className="pt-1 font-medium">{step}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    </div>
                </section>
                )}

                {/* Article Section */}
                {!isPreview && pack.article_content && pack.article_content.trim() !== '' && (
                <section id="article" className="scroll-mt-32">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-rose-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                            <BookOpen className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground">
                            {pack.article_title || 'Article de la semaine'}
                        </h2>
                    </div>
                    <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl border-2 border-rose-400 shadow-[6px_6px_0px_0px_#f43f5e] p-6 md:p-10">
                        <div className="prose prose-lg max-w-none">
                            <div className="text-foreground font-medium whitespace-pre-line leading-relaxed">
                                {/* Parse recipe links [[recipe:ID|text]] */}
                                {pack.article_content.split(/(\[\[recipe:[^\]]+\]\])/).map((part, i) => {
                                    const match = part.match(/\[\[recipe:([^|]+)\|([^\]]+)\]\]/);
                                    if (match) {
                                        const recipeId = match[1];
                                        const linkText = match[2];
                                        return (
                                            <Link 
                                                key={i} 
                                                to={`/recipes/${recipeId}`}
                                                className="text-rose-600 hover:text-rose-800 underline decoration-2 decoration-rose-300 hover:decoration-rose-500 font-bold"
                                            >
                                                {linkText}
                                            </Link>
                                        );
                                    }
                                    return <span key={i}>{part}</span>;
                                })}
                            </div>
                        </div>
                    </div>
                </section>
                )}

                {/* Nos Recommandations */}
                {!isPreview && pack.recommendations && (
                <section id="recommandations" className="scroll-mt-32">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-indigo-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                            <Sparkles className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground">
                            Nos Recommandations
                        </h2>
                    </div>
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl border-2 border-indigo-400 shadow-[6px_6px_0px_0px_#6366f1] p-6 md:p-10">
                        <div className="prose prose-lg max-w-none">
                            <p className="text-foreground font-medium whitespace-pre-line leading-relaxed">
                                {pack.recommendations}
                            </p>
                        </div>
                    </div>
                </section>
                )}

                {/* Clôture */}
                {!isPreview && (
                <section className="scroll-mt-32">
                    <div className="bg-gradient-cameleon rounded-3xl p-10 md:p-16 text-center border-4 border-foreground shadow-[8px_8px_0px_0px_#1e293b] relative overflow-hidden">
                        <div className="absolute top-6 left-6 w-16 h-16 border-4 border-white/20 rounded-full" />
                        <div className="absolute bottom-6 right-6 w-12 h-12 border-4 border-white/20 rounded-2xl rotate-12" />
                        
                        <div className="relative z-10">
                            <h2 className="font-heading text-4xl md:text-5xl font-black text-white mb-4">
                                Félicitations!
                            </h2>
                            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                                {pack.closing_message}
                            </p>
                            <Link to="/recipes">
                                <Button className="bg-white text-foreground hover:bg-white/90 rounded-full px-10 py-6 font-heading font-bold text-lg border-2 border-foreground shadow-pop hover:shadow-pop-hover hover:-translate-y-1 transition-all">
                                    Explorer plus de recettes
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </section>
                )}

                {/* Signature */}
                <footer className="text-center py-10 border-t-2 border-foreground">
                    <p className="text-muted-foreground mb-2 font-medium">Préparé avec ❤️ par</p>
                    <p className="font-heading font-black text-2xl text-foreground">
                        Recettes <span className="text-gradient-cameleon">Caméléon</span>
                    </p>
                    <p className="text-muted-foreground mt-2">
                        Votre partenaire pour cuisiner malin et économiser gros!
                    </p>
                </footer>
            </main>
        </div>
    );
};

export default WeeklyPackPage;
